import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clapperboard,
  Eye,
  ImageIcon,
  Landmark,
  Loader2,
  Pencil,
  RotateCcw,
  Trash2,
  Upload,
  BookOpen,
} from 'lucide-react'
import { MarkdownArticle } from '@/components/creative-lab/MarkdownArticle'
import { ReferenceImageSelector } from '@/components/generation/ReferenceImageSelector'
import { AssetPickerModal } from '@/components/library/AssetPickerModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  ManualProductEditor,
  type ManualProductDraft,
} from '@/components/products/ManualProductEditor'
import { ProductImportFromUrlCard } from '@/components/products/ProductImportFromUrlCard'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { readDocumentLikeFile } from '@/lib/documentInput'
import { sortNovelIdeaTags } from '@/lib/novelIdeaTags'
import { filterValidMediaUrls, formatDateTime, isVideoUrl } from '@/lib/utils'
import type {
  Asset,
  Idea,
  NovelParseResult,
  NovelScenario,
  Product,
  VerticalIdeaMarketAnalysisRequestFields,
  VerticalIdeasOutputMode,
  VerticalIdeasProductType,
} from '@/types'

const DEFAULT_IDEA_COUNT = 20
const MAX_MARKET_ANALYSIS_CHARS = 60000
const IDEA_FORMAT_OPTIONS: Array<{
  value: VerticalIdeasOutputMode
  label: string
  description: string
}> = [
  {
    value: 'mixed',
    label: 'Mixed',
    description: 'Default mix with mostly image ideas plus a smaller share of video hooks.',
  },
  {
    value: 'image',
    label: 'Image Only',
    description: 'Every idea stays as a single-frame image concept.',
  },
  {
    value: 'video',
    label: 'Video Only',
    description: 'Every idea stays as an 8-10 second short-form video hook.',
  },
]

type ProductMode = 'existing' | 'new'
type NovelSourceMode = 'url' | 'file' | 'text'
type TypedCreateMode = 'url' | 'manual'
type SetupAssetPickerMode = 'logo' | 'primary_reference' | 'product_images' | null
type NovelWorkspaceTab = 'text' | 'scenarios' | 'ideas'
type SetupWizardStepKey =
  | 'product_mode'
  | 'existing_product'
  | 'novel_source'
  | 'novel_input'
  | 'typed_create_mode'
  | 'typed_input'

type MarketAnalysisSnapshot = {
  content: string
  fileName: string | null
  fileMimeType: string | null
}

type ExtractedNovelScenarioState = {
  scenarios: NovelScenario[]
  confirmedIds: string[]
}

const EMPTY_MANUAL_PRODUCT: ManualProductDraft = {
  product_name: '',
  description: '',
  selling_points: [],
  target_market: '',
  target_audience: '',
  price: '',
  language: 'en',
  keywords: [],
  competitor_brands: [],
}

async function fetchAllVerticalIdeas(
  productId: string,
  ideaSource: string
): Promise<{ items: Idea[]; total: number; offset: number; limit: number }> {
  const pageSize = 100
  const allItems: Idea[] = []
  let offset = 0
  let total = 0

  while (true) {
    const page = await api.getIdeas(productId, {
      source: ideaSource,
      limit: pageSize,
      offset,
      sort: 'created_at_desc',
    })
    if (offset === 0) {
      total = page.total
    }
    allItems.push(...page.items)
    if (page.items.length === 0 || allItems.length >= page.total) {
      break
    }
    offset += page.items.length
  }

  return {
    items: allItems,
    total,
    offset: 0,
    limit: allItems.length,
  }
}

function getIdeaSource(productType: VerticalIdeasProductType): string {
  if (productType === 'ecommerce') {
    return 'vertical_ideas_ecommerce'
  }
  if (productType === 'finance') {
    return 'vertical_ideas_finance'
  }
  return 'vertical_ideas_novel'
}

function extractVerticalIdeasMetadata(product: Product | null): Record<string, unknown> | null {
  if (!product?.metadata || typeof product.metadata !== 'object') {
    return null
  }
  const verticalIdeas = product.metadata.vertical_ideas
  return verticalIdeas && typeof verticalIdeas === 'object'
    ? (verticalIdeas as Record<string, unknown>)
    : null
}

function extractNovelScenarioState(verticalMetadata: Record<string, unknown> | null): ExtractedNovelScenarioState {
  if (!verticalMetadata || typeof verticalMetadata !== 'object') {
    return { scenarios: [], confirmedIds: [] }
  }

  const rawScenarios = verticalMetadata.scenarios
  if (!rawScenarios || typeof rawScenarios !== 'object') {
    return { scenarios: [], confirmedIds: [] }
  }

  const scenarioRecord = rawScenarios as Record<string, unknown>
  const confirmedIds = Array.isArray(scenarioRecord.confirmed_ids)
    ? Array.from(
        new Set(
          scenarioRecord.confirmed_ids
            .map((item) => (typeof item === 'string' ? item.trim() : ''))
            .filter(Boolean)
        )
      )
    : []

  const scenarios = Array.isArray(scenarioRecord.items)
    ? scenarioRecord.items
        .map((item) => {
          if (!item || typeof item !== 'object') return null
          const record = item as Record<string, unknown>
          const id = typeof record.id === 'string' ? record.id.trim() : ''
          const scene_description =
            typeof record.scene_description === 'string' ? record.scene_description.trim() : ''
          const chapter_reference =
            typeof record.chapter_reference === 'string' ? record.chapter_reference.trim() : ''
          if (!id || !scene_description || !chapter_reference) return null
          const toStringArray = (value: unknown): string[] =>
            Array.isArray(value)
              ? Array.from(
                  new Set(
                    value
                      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
                      .filter(Boolean)
                  )
                )
              : []
          return {
            id,
            scene_description,
            characters: toStringArray(record.characters),
            emotions: toStringArray(record.emotions),
            visual_motifs: toStringArray(record.visual_motifs),
            chapter_reference,
            confirmed: confirmedIds.includes(id),
          } satisfies NovelScenario
        })
        .filter((item): item is NovelScenario => Boolean(item))
    : []

  return { scenarios, confirmedIds }
}

function extractAssetId(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function extractAssetIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }
  const items: string[] = []
  const seen = new Set<string>()
  for (const rawItem of value) {
    if (typeof rawItem !== 'string') continue
    const normalized = rawItem.trim()
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    items.push(normalized)
  }
  return items
}

function dedupeAssetIds(values: Array<string | null | undefined>): string[] {
  const items: string[] = []
  const seen = new Set<string>()
  for (const rawValue of values) {
    const normalized = typeof rawValue === 'string' ? rawValue.trim() : ''
    if (!normalized || seen.has(normalized)) continue
    seen.add(normalized)
    items.push(normalized)
  }
  return items
}

function stepLabel(step: string | null | undefined): string {
  switch (step) {
    case 'queued':
      return 'Queued'
    case 'generate_ideas':
      return 'Generating Ideas'
    case 'completed':
      return 'Completed'
    case 'failed':
      return 'Failed'
    default:
      return 'Ready'
  }
}

function buildNovelPreviewMarkdown(params: {
  sourceMode: NovelSourceMode
  sourceUrl: string
  fileName: string
  rawText: string
}): string {
  const { sourceMode, sourceUrl, fileName, rawText } = params
  const sections = [
    '# Parsed Novel Content',
    '',
    sourceMode === 'url' && sourceUrl ? `**Source URL:** [${sourceUrl}](${sourceUrl})` : '',
    sourceMode === 'file' && fileName ? `**Source File:** ${fileName}` : '',
    sourceMode === 'text' ? '**Source:** Pasted text' : '',
    '',
    '## Extracted Text',
    '',
    rawText.trim() || '_No text extracted._',
  ]
  return sections.filter(Boolean).join('\n')
}

function normalizeMarketAnalysisContent(rawValue: string): { content: string; truncated: boolean } {
  const normalized = rawValue.replace(/\r\n?/g, '\n').trim()
  if (!normalized) {
    return { content: '', truncated: false }
  }
  if (normalized.length <= MAX_MARKET_ANALYSIS_CHARS) {
    return { content: normalized, truncated: false }
  }
  return {
    content: normalized.slice(0, MAX_MARKET_ANALYSIS_CHARS).trimEnd(),
    truncated: true,
  }
}

function buildMarketAnalysisSnapshot(
  content: string,
  fileName: string | null,
  fileMimeType: string | null
): MarketAnalysisSnapshot | null {
  const normalized = normalizeMarketAnalysisContent(content)
  if (!normalized.content) {
    return null
  }
  return {
    content: normalized.content,
    fileName: fileName || null,
    fileMimeType: fileMimeType || null,
  }
}

function serializeMarketAnalysisSnapshot(snapshot: MarketAnalysisSnapshot | null): string {
  return JSON.stringify(snapshot || null)
}

function extractMarketAnalysisSnapshot(metadata: Record<string, unknown> | null): MarketAnalysisSnapshot | null {
  if (!metadata || typeof metadata !== 'object') return null
  const verticalIdeas = metadata.vertical_ideas
  if (!verticalIdeas || typeof verticalIdeas !== 'object') return null
  const report = (verticalIdeas as Record<string, unknown>).market_analysis_report
  if (!report || typeof report !== 'object') return null
  const reportRecord = report as Record<string, unknown>
  const content = typeof reportRecord.content === 'string' ? reportRecord.content : ''
  const fileName = typeof reportRecord.source_file_name === 'string' ? reportRecord.source_file_name : null
  const fileMimeType =
    typeof reportRecord.source_file_mime_type === 'string' ? reportRecord.source_file_mime_type : null
  return buildMarketAnalysisSnapshot(content, fileName, fileMimeType)
}

function buildMarketAnalysisMetadataBlock(
  snapshot: MarketAnalysisSnapshot | null
): Record<string, unknown> | undefined {
  if (!snapshot) return undefined
  return {
    content: snapshot.content,
    source_file_name: snapshot.fileName,
    source_file_mime_type: snapshot.fileMimeType,
    content_chars: snapshot.content.length,
    updated_at: new Date().toISOString(),
  }
}

function buildTypedVerticalIdeasMetadata(params: {
  productType: Extract<VerticalIdeasProductType, 'ecommerce' | 'finance'>
  sourceMode: 'url' | 'manual'
  currentMetadata: Record<string, unknown> | null
  logoAssetId?: string | null
  productImageAssetIds?: string[]
  primaryReferenceAssetId?: string | null
}): Record<string, unknown> {
  const verticalIdeas = params.currentMetadata ? { ...params.currentMetadata } : {}
  verticalIdeas.product_type = params.productType
  verticalIdeas.source_mode = params.sourceMode

  if (params.logoAssetId) {
    verticalIdeas.logo_asset_id = params.logoAssetId
  } else {
    delete verticalIdeas.logo_asset_id
  }

  if (params.productImageAssetIds && params.productImageAssetIds.length > 0) {
    verticalIdeas.product_image_asset_ids = params.productImageAssetIds
  } else {
    delete verticalIdeas.product_image_asset_ids
  }

  if (params.primaryReferenceAssetId) {
    verticalIdeas.primary_reference_asset_id = params.primaryReferenceAssetId
  } else {
    delete verticalIdeas.primary_reference_asset_id
  }

  return verticalIdeas
}

function mergeTypedMetadata(params: {
  product: Product
  productType: Extract<VerticalIdeasProductType, 'ecommerce' | 'finance'>
  sourceMode: 'url' | 'manual'
  logoAssetId?: string | null
  productImageAssetIds?: string[]
  primaryReferenceAssetId?: string | null
}): Record<string, unknown> {
  const currentMetadata =
    params.product.metadata && typeof params.product.metadata === 'object'
      ? { ...params.product.metadata }
      : {}
  currentMetadata.vertical_ideas = buildTypedVerticalIdeasMetadata({
    productType: params.productType,
    sourceMode: params.sourceMode,
    currentMetadata: extractVerticalIdeasMetadata(params.product),
    logoAssetId: params.logoAssetId,
    productImageAssetIds: params.productImageAssetIds,
    primaryReferenceAssetId: params.primaryReferenceAssetId,
  })
  return currentMetadata
}

function createChecklistStatus(ready: boolean, readyLabel = 'Ready', missingLabel = 'Missing') {
  return ready ? readyLabel : missingLabel
}

export function VerticalIdeasPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [productType, setProductType] = useState<VerticalIdeasProductType>('novel')
  const [productMode, setProductMode] = useState<ProductMode>('existing')
  const [selectedProductId, setSelectedProductId] = useState('')
  const [novelSourceMode, setNovelSourceMode] = useState<NovelSourceMode>('url')
  const [typedCreateMode, setTypedCreateMode] = useState<TypedCreateMode>('url')
  const [sourceUrl, setSourceUrl] = useState('')
  const [sourceFile, setSourceFile] = useState<File | null>(null)
  const [sourceText, setSourceText] = useState('')
  const [typedSourceUrl, setTypedSourceUrl] = useState('')
  const [typedManualProduct, setTypedManualProduct] = useState<ManualProductDraft>(EMPTY_MANUAL_PRODUCT)
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null)
  const [hasStartedCurrentRun, setHasStartedCurrentRun] = useState(false)
  const [activeTab, setActiveTab] = useState<NovelWorkspaceTab>('scenarios')
  const [parsedNovelPreview, setParsedNovelPreview] = useState<NovelParseResult | null>(null)
  const [ideaCountInput, setIdeaCountInput] = useState<number>(DEFAULT_IDEA_COUNT)
  const [analysisContent, setAnalysisContent] = useState('')
  const [analysisFileName, setAnalysisFileName] = useState<string | null>(null)
  const [analysisFileMimeType, setAnalysisFileMimeType] = useState<string | null>(null)
  const [analysisPreview, setAnalysisPreview] = useState(false)
  const [analysisPanelExpanded, setAnalysisPanelExpanded] = useState(false)
  const [savedAnalysisSnapshot, setSavedAnalysisSnapshot] = useState<MarketAnalysisSnapshot | null>(null)
  const [hasUnsavedAnalysisChanges, setHasUnsavedAnalysisChanges] = useState(false)
  const [hydratedAnalysisProductId, setHydratedAnalysisProductId] = useState<string | null>(null)
  const [selectedTag, setSelectedTag] = useState('all')
  const [ideaFormatMode, setIdeaFormatMode] = useState<VerticalIdeasOutputMode>('mixed')
  const [assetPickerMode, setAssetPickerMode] = useState<SetupAssetPickerMode>(null)
  const [wizardStepIndex, setWizardStepIndex] = useState(0)

  const analysisFileInputRef = useRef<HTMLInputElement | null>(null)

  const verticalIdeaSource = useMemo(() => getIdeaSource(productType), [productType])

  const { data: products } = useQuery({
    queryKey: ['products', 'vertical-ideas'],
    queryFn: () => api.getProducts(0, 100),
  })

  const selectedProductQuery = useQuery({
    queryKey: ['product', selectedProductId, 'vertical-ideas'],
    queryFn: () => api.getProduct(selectedProductId),
    enabled: !!selectedProductId,
  })

  const parseUrlMutation = useMutation({
    mutationFn: (url: string) => api.parseVerticalNovelUrl(url),
  })

  const parseTextMutation = useMutation({
    mutationFn: (rawText: string) => api.parseVerticalNovelText(rawText),
  })

  const parseFileMutation = useMutation({
    mutationFn: (file: File) => api.parseVerticalNovelFile(file),
  })

  const createNovelProductMutation = useMutation({
    mutationFn: (product: {
      product_name: string
      description: string
      selling_points: string
      target_audience: string
      language: string
      keywords: string[]
      competitor_brands: string[]
      source_url?: string
      metadata: Record<string, unknown>
    }) => api.createProduct(product),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products', 'vertical-ideas'] })
    },
  })

  const createTypedProductFromUrlMutation = useMutation({
    mutationFn: (params: { url: string; productType: 'ecommerce' | 'finance' }) =>
      api.createProductFromUrl(params.url, params.productType),
  })

  const createTypedManualProductMutation = useMutation({
    mutationFn: (params: {
      productType: 'ecommerce' | 'finance'
      form: ManualProductDraft
    }) =>
      api.createProduct({
        product_name: params.form.product_name,
        description: params.form.description || undefined,
        selling_points: params.form.selling_points.join('\n') || undefined,
        target_market: params.form.target_market || undefined,
        target_audience: params.form.target_audience || undefined,
        price: params.form.price || undefined,
        language: params.form.language || 'en',
        keywords: params.form.keywords,
        competitor_brands: params.form.competitor_brands,
        metadata: {
          vertical_ideas: {
            product_type: params.productType,
            source_mode: 'manual',
          },
        },
      }),
  })

  const updateProductMutation = useMutation({
    mutationFn: (params: { productId: string; data: Record<string, unknown> }) =>
      api.updateProduct(params.productId, params.data),
    onSuccess: async (product) => {
      queryClient.setQueryData(['product', product.id, 'vertical-ideas'], product)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products', 'vertical-ideas'] }),
        queryClient.invalidateQueries({ queryKey: ['product', product.id, 'vertical-ideas'] }),
      ])
    },
  })

  const generateNovelScenariosMutation = useMutation({
    mutationFn: (params: { productId: string }) => api.generateVerticalNovelScenarios(params.productId, {}),
    onSuccess: async (response) => {
      queryClient.setQueryData(['product', response.product.id, 'vertical-ideas'], response.product)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products', 'vertical-ideas'] }),
        queryClient.invalidateQueries({ queryKey: ['product', response.product.id, 'vertical-ideas'] }),
      ])
    },
  })

  const deleteNovelScenarioMutation = useMutation({
    mutationFn: (params: { productId: string; scenarioId: string }) =>
      api.deleteVerticalNovelScenario(params.productId, params.scenarioId),
    onSuccess: async (response) => {
      queryClient.setQueryData(['product', response.product.id, 'vertical-ideas'], response.product)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['products', 'vertical-ideas'] }),
        queryClient.invalidateQueries({ queryKey: ['product', response.product.id, 'vertical-ideas'] }),
      ])
    },
  })

  const startMutation = useMutation({
    mutationFn: (payload: {
      product_type: VerticalIdeasProductType
      product_id: string
      idea_count?: number
      idea_format?: VerticalIdeasOutputMode
      analysis?: VerticalIdeaMarketAnalysisRequestFields
    }) =>
      api.startVerticalIdeas({
        product_type: payload.product_type,
        product_id: payload.product_id,
        idea_count: payload.idea_count,
        idea_format: payload.idea_format,
        ...payload.analysis,
      }),
    onSuccess: (data) => {
      setActiveTaskId(data.task_id)
      setHasStartedCurrentRun(true)
      setActiveTab('ideas')
      toast({ title: 'Idea generation started' })
    },
  })

  const removeMarketAnalysisMutation = useMutation({
    mutationFn: (productId: string) => api.removeVerticalIdeaMarketAnalysis(productId),
  })

  const resultQuery = useQuery({
    queryKey: ['vertical-ideas-result', activeTaskId],
    queryFn: () => api.getVerticalIdeasResult(activeTaskId!),
    enabled: !!activeTaskId,
    refetchInterval: (query) => {
      const currentStatus = query.state.data?.status
      return currentStatus === 'pending' || currentStatus === 'processing' ? 2500 : false
    },
  })

  const allVerticalIdeasQuery = useQuery({
    queryKey: ['vertical-ideas-cumulative', selectedProductId, verticalIdeaSource],
    queryFn: () => fetchAllVerticalIdeas(selectedProductId, verticalIdeaSource),
    enabled: !!selectedProductId,
    refetchInterval: () => {
      const status = resultQuery.data?.status
      return status === 'pending' || status === 'processing' || startMutation.isPending ? 2500 : false
    },
  })

  const selectedProductDetail = selectedProductQuery.data || null
  const selectedVerticalIdeasMetadata = useMemo(
    () => extractVerticalIdeasMetadata(selectedProductDetail),
    [selectedProductDetail]
  )
  const selectedNovelScenarioState = useMemo(
    () => extractNovelScenarioState(selectedVerticalIdeasMetadata),
    [selectedVerticalIdeasMetadata]
  )
  const selectedNovelScenarios = selectedNovelScenarioState.scenarios
  const selectedNovelConfirmedScenarioCount =
    selectedNovelScenarioState.confirmedIds.length || selectedNovelScenarios.length
  const selectedLogoAssetId = extractAssetId(selectedVerticalIdeasMetadata?.logo_asset_id)
  const selectedPrimaryReferenceAssetId = extractAssetId(selectedVerticalIdeasMetadata?.primary_reference_asset_id)
  const selectedProductImageAssetIds = extractAssetIds(selectedVerticalIdeasMetadata?.product_image_asset_ids)
  const selectedTypedSourceMode =
    selectedVerticalIdeasMetadata?.source_mode === 'url'
      ? 'url'
      : selectedProductDetail?.source_url
        ? 'url'
        : 'manual'

  const selectedTypedAssetIds = useMemo(
    () => dedupeAssetIds([selectedLogoAssetId, selectedPrimaryReferenceAssetId, ...selectedProductImageAssetIds]),
    [selectedLogoAssetId, selectedPrimaryReferenceAssetId, selectedProductImageAssetIds]
  )

  const selectedTypedAssetsQuery = useQuery({
    queryKey: ['vertical-ideas-typed-assets', selectedProductId, selectedTypedAssetIds],
    queryFn: () => Promise.all(selectedTypedAssetIds.map((assetId) => api.getAsset(assetId))),
    enabled: productType !== 'novel' && selectedTypedAssetIds.length > 0,
  })

  const selectedTypedAssetMap = useMemo(() => {
    const map = new Map<string, Asset>()
    for (const asset of selectedTypedAssetsQuery.data || []) {
      map.set(asset.id, asset)
    }
    return map
  }, [selectedTypedAssetsQuery.data])

  const selectedLogoAsset = selectedLogoAssetId ? selectedTypedAssetMap.get(selectedLogoAssetId) || null : null
  const selectedPrimaryReferenceAsset = selectedPrimaryReferenceAssetId
    ? selectedTypedAssetMap.get(selectedPrimaryReferenceAssetId) || null
    : null
  const selectedProductImageAssets = selectedProductImageAssetIds
    .map((assetId) => selectedTypedAssetMap.get(assetId) || null)
    .filter((asset): asset is Asset => Boolean(asset))

  const filteredProducts = useMemo(
    () => (products?.items || []).filter((product) => product.product_type === productType),
    [productType, products?.items]
  )
  const wizardSteps = useMemo<
    Array<{ key: SetupWizardStepKey; label: string; title: string; description: string }>
  >(() => {
    if (productMode === 'existing') {
      return [
        {
          key: 'product_mode',
          label: 'Path',
          title: 'Choose Product Path',
          description: 'Decide whether to continue from a saved product or create a new one.',
        },
        {
          key: 'existing_product',
          label: 'Product',
          title: 'Select Existing Product',
          description: 'Pick the saved product you want to continue with.',
        },
      ]
    }
    if (productType === 'novel') {
      return [
        {
          key: 'product_mode',
          label: 'Path',
          title: 'Choose Product Path',
          description: 'Start from an existing novel product or create one from a new source.',
        },
        {
          key: 'novel_source',
          label: 'Source',
          title: 'Choose Novel Source',
          description: 'Select whether the novel text comes from a URL, pasted text, or an uploaded file.',
        },
        {
          key: 'novel_input',
          label: 'Prepare',
          title: 'Prepare Novel Input',
          description: 'Paste, upload, or fetch the content, then review the preview and create the product.',
        },
      ]
    }
    return [
      {
        key: 'product_mode',
        label: 'Path',
        title: 'Choose Product Path',
        description: 'Use a saved product or create a new one for this vertical.',
      },
      {
        key: 'typed_create_mode',
        label: 'Method',
        title: 'Choose Creation Method',
        description: 'Pick URL import or manual entry for the new product.',
      },
      {
        key: 'typed_input',
        label: 'Prepare',
        title: 'Prepare Product Input',
        description: 'Import or enter the product details before moving to the setup checklist.',
      },
    ]
  }, [productMode, productType])
  const currentWizardStep = wizardSteps[Math.min(wizardStepIndex, wizardSteps.length - 1)] || wizardSteps[0]
  const wizardSummaryItems = useMemo(() => {
    const items: Array<{ label: string; value: string }> = [
      { label: 'Path', value: productMode === 'existing' ? 'Use Existing' : 'Create New' },
    ]
    if (productMode === 'existing') {
      if (selectedProductDetail?.product_name) {
        items.push({ label: 'Product', value: selectedProductDetail.product_name })
      }
      return items
    }
    if (productType === 'novel') {
      items.push({
        label: 'Source',
        value: novelSourceMode === 'url' ? 'URL' : novelSourceMode === 'file' ? 'File' : 'Text',
      })
      if (parsedNovelPreview?.parsed_fields.product_name) {
        items.push({ label: 'Parsed', value: parsedNovelPreview.parsed_fields.product_name })
      }
      return items
    }
    items.push({ label: 'Method', value: typedCreateMode === 'url' ? 'URL Import' : 'Manual Entry' })
    if (typedCreateMode === 'manual' && typedManualProduct.product_name.trim()) {
      items.push({ label: 'Draft', value: typedManualProduct.product_name.trim() })
    }
    if (selectedProductDetail?.product_name) {
      items.push({ label: 'Ready', value: selectedProductDetail.product_name })
    }
    return items
  }, [
    parsedNovelPreview?.parsed_fields.product_name,
    productMode,
    productType,
    novelSourceMode,
    selectedProductDetail?.product_name,
    selectedProductDetail,
    typedCreateMode,
    typedManualProduct.product_name,
  ])
  const isLastWizardStep = wizardStepIndex >= wizardSteps.length - 1
  const canAdvanceWizard =
    currentWizardStep?.key === 'existing_product'
      ? Boolean(selectedProductId)
      : true

  const allVerticalIdeas = allVerticalIdeasQuery.data?.items || []
  const availableIdeaTags = useMemo(
    () => (productType === 'novel' ? sortNovelIdeaTags(allVerticalIdeas.flatMap((idea) => idea.tags || [])) : []),
    [allVerticalIdeas, productType]
  )
  const filteredVerticalIdeas = useMemo(() => {
    if (productType !== 'novel' || selectedTag === 'all') {
      return allVerticalIdeas
    }
    return allVerticalIdeas.filter((idea) => idea.tags.includes(selectedTag))
  }, [allVerticalIdeas, productType, selectedTag])

  const selectedNovelRawText = useMemo(() => {
    const rawText = selectedVerticalIdeasMetadata?.raw_text
    return typeof rawText === 'string' ? rawText : ''
  }, [selectedVerticalIdeasMetadata])

  const selectedNovelSourceMode = useMemo(() => {
    const sourceModeValue = selectedVerticalIdeasMetadata?.source_mode
    if (sourceModeValue === 'url' || sourceModeValue === 'file' || sourceModeValue === 'text') {
      return sourceModeValue
    }
    const sourceFileName = selectedVerticalIdeasMetadata?.source_file_name
    if (typeof sourceFileName === 'string' && sourceFileName.trim()) {
      return 'file'
    }
    return selectedProductDetail?.source_url ? 'url' : 'text'
  }, [selectedProductDetail?.source_url, selectedVerticalIdeasMetadata])

  const selectedNovelSourceFileName = useMemo(() => {
    const fileName = selectedVerticalIdeasMetadata?.source_file_name
    return typeof fileName === 'string' ? fileName : ''
  }, [selectedVerticalIdeasMetadata])

  const selectedSavedAnalysisSnapshot = useMemo(
    () => extractMarketAnalysisSnapshot(selectedProductDetail?.metadata || null),
    [selectedProductDetail]
  )

  const novelPreviewMarkdown = useMemo(
    () =>
      parsedNovelPreview
        ? buildNovelPreviewMarkdown({
            sourceMode: novelSourceMode,
            sourceUrl: sourceUrl.trim(),
            fileName: sourceFile?.name || '',
            rawText: parsedNovelPreview.raw_text,
          })
        : '',
    [parsedNovelPreview, novelSourceMode, sourceUrl, sourceFile]
  )

  const selectedNovelTextMarkdown = useMemo(() => {
    if (!selectedNovelRawText) return ''
    return buildNovelPreviewMarkdown({
      sourceMode: selectedNovelSourceMode,
      sourceUrl: selectedProductDetail?.source_url || '',
      fileName: selectedNovelSourceFileName,
      rawText: selectedNovelRawText,
    })
  }, [selectedNovelRawText, selectedNovelSourceMode, selectedNovelSourceFileName, selectedProductDetail?.source_url])

  const currentNovelStyle = useMemo(() => {
    if (parsedNovelPreview?.parsed_fields.novel_style && productMode === 'new') {
      return parsedNovelPreview.parsed_fields.novel_style
    }
    const rawStyle = selectedVerticalIdeasMetadata?.novel_style
    return typeof rawStyle === 'string' && rawStyle.trim() ? rawStyle : 'general fiction'
  }, [parsedNovelPreview?.parsed_fields.novel_style, productMode, selectedVerticalIdeasMetadata])

  const hasSavedNovelScenarios = selectedNovelScenarios.length > 0
  const hasAnyVerticalIdeas = allVerticalIdeas.length > 0
  const taskStatus = resultQuery.data?.status
  const currentStep = resultQuery.data?.current_step
  const requestedIdeaCount = resultQuery.data?.requested_count ?? 0
  const successfulIdeaCount = resultQuery.data?.success_count ?? 0
  const completedBatchCount = resultQuery.data?.completed_batch_count ?? 0
  const totalBatchCount = resultQuery.data?.total_batch_count ?? 0
  const executionMetricLabel = productType === 'novel' ? 'Execution' : 'Batches'
  const executionMetricValue =
    productType === 'novel' ? 'All concurrent' : `${completedBatchCount}/${totalBatchCount}`
  const isIdeaGenerationInFlight =
    startMutation.isPending || taskStatus === 'pending' || taskStatus === 'processing'
  const shouldHideInitialGenerateIdeasButton = isIdeaGenerationInFlight && !hasAnyVerticalIdeas
  const isBusy =
    parseUrlMutation.isPending ||
    parseTextMutation.isPending ||
    parseFileMutation.isPending ||
    createNovelProductMutation.isPending ||
    createTypedProductFromUrlMutation.isPending ||
    createTypedManualProductMutation.isPending ||
    generateNovelScenariosMutation.isPending ||
    deleteNovelScenarioMutation.isPending ||
    updateProductMutation.isPending ||
    startMutation.isPending ||
    removeMarketAnalysisMutation.isPending

  const ecommerceChecklist = useMemo(() => {
    const hasProductName = Boolean(selectedProductDetail?.product_name?.trim())
    const hasPrice = Boolean(selectedProductDetail?.price?.trim())
    const hasSellingPoints = Boolean(selectedProductDetail?.selling_points?.trim())
    const hasPrimaryReference = Boolean(selectedPrimaryReferenceAssetId)
    const hasEnoughProductImages = selectedProductImageAssetIds.length >= 2
    return {
      hasProductName,
      hasPrice,
      hasSellingPoints,
      hasPrimaryReference,
      hasEnoughProductImages,
      ready:
        hasProductName &&
        hasPrice &&
        hasSellingPoints &&
        hasPrimaryReference &&
        hasEnoughProductImages,
    }
  }, [
    selectedPrimaryReferenceAssetId,
    selectedProductDetail?.price,
    selectedProductDetail?.product_name,
    selectedProductDetail?.selling_points,
    selectedProductImageAssetIds.length,
  ])

  const financeChecklist = useMemo(() => {
    const hasProductName = Boolean(selectedProductDetail?.product_name?.trim())
    const hasLogo = Boolean(selectedLogoAssetId)
    const primaryMatchesLogo = Boolean(selectedLogoAssetId && selectedPrimaryReferenceAssetId === selectedLogoAssetId)
    return {
      hasProductName,
      hasLogo,
      primaryMatchesLogo,
      ready: hasProductName && hasLogo && primaryMatchesLogo,
    }
  }, [selectedLogoAssetId, selectedPrimaryReferenceAssetId, selectedProductDetail?.product_name])

  useEffect(() => {
    if (productType !== 'novel' || productMode !== 'existing' || !selectedProductId) return
    const nextSnapshot = selectedSavedAnalysisSnapshot
    const nextSerialized = serializeMarketAnalysisSnapshot(nextSnapshot)
    const currentSerialized = serializeMarketAnalysisSnapshot(savedAnalysisSnapshot)
    if (selectedProductId === hydratedAnalysisProductId && nextSerialized === currentSerialized) {
      return
    }
    setAnalysisContent(nextSnapshot?.content ?? '')
    setAnalysisFileName(nextSnapshot?.fileName ?? null)
    setAnalysisFileMimeType(nextSnapshot?.fileMimeType ?? null)
    setSavedAnalysisSnapshot(nextSnapshot)
    setHydratedAnalysisProductId(selectedProductId)
    setAnalysisPreview(false)
  }, [
    hydratedAnalysisProductId,
    productMode,
    productType,
    selectedProductId,
    selectedSavedAnalysisSnapshot,
    savedAnalysisSnapshot,
  ])

  useEffect(() => {
    if (productType !== 'novel') {
      setHasUnsavedAnalysisChanges(false)
      return
    }
    const currentSnapshot = buildMarketAnalysisSnapshot(
      analysisContent,
      analysisFileName,
      analysisFileMimeType
    )
    setHasUnsavedAnalysisChanges(
      serializeMarketAnalysisSnapshot(currentSnapshot) !== serializeMarketAnalysisSnapshot(savedAnalysisSnapshot)
    )
  }, [analysisContent, analysisFileName, analysisFileMimeType, productType, savedAnalysisSnapshot])

  useEffect(() => {
    setHasStartedCurrentRun(false)
    setActiveTaskId(null)
    setIdeaCountInput(DEFAULT_IDEA_COUNT)
    setActiveTab(productType === 'novel' ? 'scenarios' : 'ideas')
    setSelectedTag('all')
    setIdeaFormatMode('mixed')
  }, [productType, selectedProductId])

  useEffect(() => {
    setWizardStepIndex((current) => Math.min(current, Math.max(wizardSteps.length - 1, 0)))
  }, [wizardSteps.length])

  useEffect(() => {
    if (selectedTag === 'all') return
    if (availableIdeaTags.includes(selectedTag)) return
    setSelectedTag('all')
  }, [availableIdeaTags, selectedTag])

  useEffect(() => {
    if (!selectedProductId) return
    if (taskStatus !== 'completed' && taskStatus !== 'failed') return
    queryClient.invalidateQueries({ queryKey: ['vertical-ideas-cumulative', selectedProductId, verticalIdeaSource] })
    queryClient.invalidateQueries({ queryKey: ['product', selectedProductId, 'vertical-ideas'] })
  }, [queryClient, selectedProductId, taskStatus, verticalIdeaSource])

  const clearNovelPreview = () => {
    setParsedNovelPreview(null)
  }

  const resetAnalysisEditor = () => {
    setAnalysisContent('')
    setAnalysisFileName(null)
    setAnalysisFileMimeType(null)
    setAnalysisPreview(false)
    setAnalysisPanelExpanded(false)
    setSavedAnalysisSnapshot(null)
    setHasUnsavedAnalysisChanges(false)
    setHydratedAnalysisProductId(null)
  }

  const applySavedAnalysisSnapshot = (snapshot: MarketAnalysisSnapshot | null) => {
    setAnalysisContent(snapshot?.content ?? '')
    setAnalysisFileName(snapshot?.fileName ?? null)
    setAnalysisFileMimeType(snapshot?.fileMimeType ?? null)
    setSavedAnalysisSnapshot(snapshot)
    setAnalysisPreview(false)
    setHasUnsavedAnalysisChanges(false)
    setHydratedAnalysisProductId(selectedProductId || null)
  }

  const syncAnalysisEditorAfterPersist = (savedSnapshot: MarketAnalysisSnapshot | null) => {
    if (savedSnapshot) {
      applySavedAnalysisSnapshot(savedSnapshot)
      return
    }
    if (savedAnalysisSnapshot || hasUnsavedAnalysisChanges) {
      applySavedAnalysisSnapshot(savedAnalysisSnapshot)
    }
  }

  const prepareAnalysisRequest = (): {
    fields: VerticalIdeaMarketAnalysisRequestFields | null
    snapshot: MarketAnalysisSnapshot | null
  } => {
    const normalized = normalizeMarketAnalysisContent(analysisContent)
    if (normalized.truncated) {
      toast({
        title: 'Report trimmed',
        description: `Market analysis reports are limited to ${MAX_MARKET_ANALYSIS_CHARS.toLocaleString()} characters.`,
      })
    }
    if (normalized.content !== analysisContent) {
      setAnalysisContent(normalized.content)
      if (!normalized.content) {
        setAnalysisFileName(null)
        setAnalysisFileMimeType(null)
      }
    }
    const snapshot = buildMarketAnalysisSnapshot(normalized.content, analysisFileName, analysisFileMimeType)
    return {
      fields: snapshot
        ? {
            analysis_content: snapshot.content,
            analysis_file_name: snapshot.fileName ?? undefined,
            analysis_file_mime_type: snapshot.fileMimeType ?? undefined,
          }
        : null,
      snapshot,
    }
  }

  const resetNewProductInputs = () => {
    setSourceUrl('')
    setSourceFile(null)
    setSourceText('')
    setTypedSourceUrl('')
    setTypedManualProduct(EMPTY_MANUAL_PRODUCT)
    clearNovelPreview()
    resetAnalysisEditor()
  }

  const resetWorkspaceState = () => {
    setSelectedProductId('')
    setActiveTaskId(null)
    setHasStartedCurrentRun(false)
    setActiveTab(productType === 'novel' ? 'scenarios' : 'ideas')
    setIdeaCountInput(DEFAULT_IDEA_COUNT)
    setAssetPickerMode(null)
    resetNewProductInputs()
  }

  const confirmDiscardUnsavedChanges = (): boolean => {
    if (productType === 'novel' && hasUnsavedAnalysisChanges) {
      return window.confirm('You have unsaved market analysis changes. Discard them?')
    }
    return true
  }

  const handleProductTypeChange = (nextType: VerticalIdeasProductType) => {
    if (!confirmDiscardUnsavedChanges()) return
    setProductType(nextType)
    setProductMode('existing')
    setNovelSourceMode('url')
    setTypedCreateMode('url')
    setWizardStepIndex(0)
    resetWorkspaceState()
  }

  const handleProductModeChange = (nextMode: ProductMode) => {
    if (!confirmDiscardUnsavedChanges()) return
    setProductMode(nextMode)
    setNovelSourceMode('url')
    setTypedCreateMode('url')
    setWizardStepIndex(1)
    resetWorkspaceState()
  }

  const handleExistingProductChange = (productId: string) => {
    if (!confirmDiscardUnsavedChanges()) return
    resetAnalysisEditor()
    setSelectedProductId(productId)
    setProductMode('existing')
    setWizardStepIndex(1)
    clearNovelPreview()
  }

  const handleNovelSourceModeChange = (nextMode: NovelSourceMode) => {
    if (!confirmDiscardUnsavedChanges()) return
    setNovelSourceMode(nextMode)
    setSourceUrl('')
    setSourceFile(null)
    setSourceText('')
    setWizardStepIndex(2)
    clearNovelPreview()
  }

  const handleTypedCreateModeChange = (nextMode: TypedCreateMode) => {
    setTypedCreateMode(nextMode)
    setWizardStepIndex(2)
  }

  const goToPreviousWizardStep = () => {
    setWizardStepIndex((current) => Math.max(current - 1, 0))
  }

  const goToNextWizardStep = () => {
    setWizardStepIndex((current) => Math.min(current + 1, wizardSteps.length - 1))
  }

  const handleAnalysisFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const rawText = await readDocumentLikeFile(file)
      const normalized = normalizeMarketAnalysisContent(rawText)
      setAnalysisContent(normalized.content)
      setAnalysisFileName(file.name)
      setAnalysisFileMimeType(file.type || null)
      setAnalysisPreview(false)
      if (normalized.truncated) {
        toast({
          title: 'Report trimmed',
          description: `Market analysis reports are limited to ${MAX_MARKET_ANALYSIS_CHARS.toLocaleString()} characters.`,
        })
      } else {
        toast({ title: `Loaded: ${file.name}` })
      }
    } catch {
      toast({
        title: 'Failed to read file',
        description: 'Try converting the document to .docx or plain text.',
        variant: 'destructive',
      })
    }
    event.target.value = ''
  }

  const handleClearAnalysisDraft = () => {
    setAnalysisContent('')
    setAnalysisFileName(null)
    setAnalysisFileMimeType(null)
    setAnalysisPreview(false)
  }

  const handleResetAnalysisToSaved = () => {
    applySavedAnalysisSnapshot(savedAnalysisSnapshot)
  }

  const handleRemoveSavedAnalysis = async () => {
    if (!selectedProductId || !savedAnalysisSnapshot) return
    const shouldRemove = window.confirm(
      'Remove the saved market analysis report from this product? This will affect future idea generation.'
    )
    if (!shouldRemove) return
    try {
      await removeMarketAnalysisMutation.mutateAsync(selectedProductId)
      resetAnalysisEditor()
      await queryClient.invalidateQueries({ queryKey: ['product', selectedProductId, 'vertical-ideas'] })
      toast({ title: 'Saved market analysis removed' })
    } catch (error: any) {
      toast({
        title: 'Failed to remove saved report',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const buildCreateNovelProductMetadata = (): Record<string, unknown> => {
    const parsedMetadata = (parsedNovelPreview?.metadata || {}) as Record<string, unknown>
    const marketAnalysisSnapshot = buildMarketAnalysisSnapshot(analysisContent, analysisFileName, analysisFileMimeType)
    if (!marketAnalysisSnapshot) {
      return parsedMetadata
    }
    const verticalIdeas =
      parsedMetadata.vertical_ideas && typeof parsedMetadata.vertical_ideas === 'object'
        ? { ...(parsedMetadata.vertical_ideas as Record<string, unknown>) }
        : {}
    verticalIdeas.market_analysis_report = buildMarketAnalysisMetadataBlock(marketAnalysisSnapshot)
    return {
      ...parsedMetadata,
      vertical_ideas: verticalIdeas,
    }
  }

  const handleParsePreview = async () => {
    try {
      clearNovelPreview()

      let parsed: NovelParseResult
      if (novelSourceMode === 'url') {
        if (!sourceUrl.trim()) {
          toast({
            title: 'Source URL required',
            description: 'Enter a URL before parsing the novel content.',
            variant: 'destructive',
          })
          return
        }
        parsed = await parseUrlMutation.mutateAsync(sourceUrl.trim())
      } else if (novelSourceMode === 'file') {
        if (!sourceFile) {
          toast({
            title: 'Upload required',
            description: 'Choose a file before parsing the novel content.',
            variant: 'destructive',
          })
          return
        }
        parsed = await parseFileMutation.mutateAsync(sourceFile)
      } else {
        if (!sourceText.trim()) {
          toast({
            title: 'Novel text required',
            description: 'Paste the novel text before parsing the content.',
            variant: 'destructive',
          })
          return
        }
        parsed = await parseTextMutation.mutateAsync(sourceText)
      }

      setParsedNovelPreview(parsed)
      toast({
        title: 'Novel content parsed',
        description: 'Review the full markdown preview, then create the product and generate scenarios.',
      })
    } catch (error: any) {
      toast({
        title: 'Failed to parse novel source',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const generateNovelScenarios = async (productId: string) => {
    const previousTotal = selectedNovelScenarios.length
    const response = await generateNovelScenariosMutation.mutateAsync({
      productId,
    })
    setActiveTab('scenarios')
    toast({
      title: previousTotal > 0 ? 'More scenarios generated' : 'Scenarios generated',
      description: `${response.total_scenarios} saved scenarios on this product.`,
    })
  }

  const startIdeaGeneration = async (
    productId: string,
    options?: {
      includePendingAnalysis?: boolean
      ideaCount?: number
      ideaFormat?: VerticalIdeasOutputMode
    }
  ) => {
    const includePendingAnalysis = options?.includePendingAnalysis ?? true
    const requestedIdeaCount =
      productType === 'novel'
        ? options?.ideaCount ?? selectedNovelConfirmedScenarioCount
        : options?.ideaCount ?? ideaCountInput
    const requestedIdeaFormat = options?.ideaFormat ?? ideaFormatMode
    const normalizedIdeaCount = Number.isFinite(requestedIdeaCount)
      ? Math.max(Math.trunc(requestedIdeaCount), 1)
      : 1
    if (productType === 'novel' && selectedNovelScenarios.length === 0) {
      toast({
        title: 'Scenarios required',
        description: 'Generate scenarios first, then delete any weak ones before generating ideas.',
        variant: 'destructive',
      })
      return
    }
    const pendingAnalysis =
      productType === 'novel' && includePendingAnalysis && hasUnsavedAnalysisChanges && analysisContent.trim()
        ? prepareAnalysisRequest()
        : { fields: null, snapshot: null }

    await startMutation.mutateAsync({
      product_type: productType,
      product_id: productId,
      idea_count: productType === 'novel' ? undefined : normalizedIdeaCount,
      idea_format: requestedIdeaFormat,
      analysis: pendingAnalysis.fields || undefined,
    })
    if (productType === 'novel') {
      syncAnalysisEditorAfterPersist(pendingAnalysis.snapshot)
    }
    queryClient.invalidateQueries({ queryKey: ['vertical-ideas-cumulative', productId, verticalIdeaSource] })
    if (pendingAnalysis.fields) {
      queryClient.invalidateQueries({ queryKey: ['product', productId, 'vertical-ideas'] })
    }
  }

  const handleCreateAndGenerateNovelScenarios = async () => {
    try {
      if (!parsedNovelPreview) {
        toast({
          title: 'Preview required',
          description: 'Parse the source and review the preview first.',
          variant: 'destructive',
        })
        return
      }

      const parsed = parsedNovelPreview
      const product = await createNovelProductMutation.mutateAsync({
        product_name: parsed.parsed_fields.product_name,
        description: parsed.parsed_fields.description,
        selling_points: parsed.parsed_fields.selling_points,
        target_audience: parsed.parsed_fields.target_audience,
        language: parsed.parsed_fields.language,
        keywords: parsed.parsed_fields.keywords,
        competitor_brands: parsed.parsed_fields.competitor_brands,
        source_url: novelSourceMode === 'url' ? sourceUrl.trim() : undefined,
        metadata: buildCreateNovelProductMetadata(),
      })

      const createdAnalysisSnapshot = buildMarketAnalysisSnapshot(
        analysisContent,
        analysisFileName,
        analysisFileMimeType
      )
      queryClient.setQueryData(['product', product.id, 'vertical-ideas'], product)
      clearNovelPreview()
      setSelectedProductId(product.id)
      setProductMode('existing')
      setSourceUrl('')
      setSourceFile(null)
      setSourceText('')
      setIdeaCountInput(DEFAULT_IDEA_COUNT)
      setActiveTab('scenarios')
      setSavedAnalysisSnapshot(createdAnalysisSnapshot)
      setAnalysisContent(createdAnalysisSnapshot?.content ?? '')
      setAnalysisFileName(createdAnalysisSnapshot?.fileName ?? null)
      setAnalysisFileMimeType(createdAnalysisSnapshot?.fileMimeType ?? null)
      setHasUnsavedAnalysisChanges(false)

      await generateNovelScenarios(product.id)
    } catch (error: any) {
      setHasStartedCurrentRun(false)
      setActiveTaskId(null)
      toast({
        title: 'Failed to create product and generate scenarios',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleCreateNewPrimaryAction = async () => {
    if (!parsedNovelPreview) {
      await handleParsePreview()
      return
    }
    await handleCreateAndGenerateNovelScenarios()
  }

  const handleGenerateScenarios = async () => {
    if (!selectedProductId) return
    try {
      await generateNovelScenarios(selectedProductId)
    } catch (error: any) {
      toast({
        title: 'Failed to generate scenarios',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleDeleteScenario = async (scenarioId: string) => {
    if (!selectedProductId) return
    const shouldDelete = window.confirm('Delete this scenario from the saved set?')
    if (!shouldDelete) return
    try {
      await deleteNovelScenarioMutation.mutateAsync({
        productId: selectedProductId,
        scenarioId,
      })
      toast({ title: 'Scenario deleted' })
    } catch (error: any) {
      toast({
        title: 'Failed to delete scenario',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleTypedProductCreated = async (product: Product) => {
    await queryClient.invalidateQueries({ queryKey: ['products', 'vertical-ideas'] })
    queryClient.setQueryData(['product', product.id, 'vertical-ideas'], product)
    setSelectedProductId(product.id)
    setProductMode('existing')
    setTypedSourceUrl('')
    setTypedManualProduct(EMPTY_MANUAL_PRODUCT)
    toast({
      title: `${productType === 'finance' ? 'Finance' : 'Ecommerce'} product ready`,
      description: 'Complete the setup checklist in the workspace before generating ideas.',
    })
  }

  const handleCreateTypedFromUrl = async () => {
    const typedProductType = productType === 'finance' ? 'finance' : 'ecommerce'
    if (!typedSourceUrl.trim()) {
      toast({
        title: 'URL required',
        description: 'Enter a URL before importing.',
        variant: 'destructive',
      })
      return
    }
    try {
      const product = await createTypedProductFromUrlMutation.mutateAsync({
        url: typedSourceUrl.trim(),
        productType: typedProductType,
      })
      await handleTypedProductCreated(product)
    } catch (error: any) {
      toast({
        title: 'Failed to import product',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleCreateTypedManual = async () => {
    const typedProductType = productType === 'finance' ? 'finance' : 'ecommerce'
    if (!typedManualProduct.product_name.trim()) {
      toast({
        title: 'Product name required',
        description: 'Enter a product name before creating the product.',
        variant: 'destructive',
      })
      return
    }
    try {
      const product = await createTypedManualProductMutation.mutateAsync({
        productType: typedProductType,
        form: typedManualProduct,
      })
      await handleTypedProductCreated(product)
    } catch (error: any) {
      toast({
        title: 'Failed to create product',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const persistTypedSetup = async (params: {
    logoAssetId?: string | null
    productImageAssetIds?: string[]
    primaryReferenceAssetId?: string | null
    screenshotAssetId?: string | null
    includeScreenshotAssetId?: boolean
  }) => {
    if (!selectedProductDetail || (productType !== 'ecommerce' && productType !== 'finance')) {
      return
    }
    const nextSourceMode = selectedTypedSourceMode
    const nextPrimaryReferenceAssetId =
      params.primaryReferenceAssetId !== undefined
        ? params.primaryReferenceAssetId
        : selectedPrimaryReferenceAssetId
    const nextLogoAssetId =
      params.logoAssetId !== undefined ? params.logoAssetId : selectedLogoAssetId
    const nextProductImageAssetIds =
      params.productImageAssetIds !== undefined
        ? params.productImageAssetIds
        : selectedProductImageAssetIds

    const metadata = mergeTypedMetadata({
      product: selectedProductDetail,
      productType,
      sourceMode: nextSourceMode,
      logoAssetId: nextLogoAssetId,
      productImageAssetIds: productType === 'ecommerce' ? nextProductImageAssetIds : [],
      primaryReferenceAssetId: nextPrimaryReferenceAssetId,
    })
    const payload: Record<string, unknown> = { metadata }
    if (params.includeScreenshotAssetId) {
      payload.screenshot_asset_id = params.screenshotAssetId ?? null
    }
    await updateProductMutation.mutateAsync({
      productId: selectedProductDetail.id,
      data: payload,
    })
  }

  const handleSetupAssetSelected = async (asset: Asset) => {
    if (!selectedProductDetail || !assetPickerMode) return
    try {
      if (assetPickerMode === 'logo') {
        if (productType === 'finance') {
          await persistTypedSetup({
            logoAssetId: asset.id,
            primaryReferenceAssetId: asset.id,
            screenshotAssetId: asset.id,
            includeScreenshotAssetId: true,
          })
        } else {
          await persistTypedSetup({ logoAssetId: asset.id })
        }
      } else if (assetPickerMode === 'primary_reference') {
        const nextProductImageAssetIds = dedupeAssetIds([asset.id, ...selectedProductImageAssetIds])
        await persistTypedSetup({
          productImageAssetIds: nextProductImageAssetIds,
          primaryReferenceAssetId: asset.id,
          screenshotAssetId: asset.id,
          includeScreenshotAssetId: true,
        })
      } else if (assetPickerMode === 'product_images') {
        const nextProductImageAssetIds = dedupeAssetIds([...selectedProductImageAssetIds, asset.id])
        const nextPrimaryReferenceAssetId = selectedPrimaryReferenceAssetId || asset.id
        await persistTypedSetup({
          productImageAssetIds: nextProductImageAssetIds,
          primaryReferenceAssetId: nextPrimaryReferenceAssetId,
          screenshotAssetId: selectedPrimaryReferenceAssetId ? undefined : nextPrimaryReferenceAssetId,
          includeScreenshotAssetId: !selectedPrimaryReferenceAssetId,
        })
      }
      setAssetPickerMode(null)
    } catch (error: any) {
      toast({
        title: 'Failed to update setup',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleRemoveProductImage = async (assetId: string) => {
    const nextProductImageAssetIds = selectedProductImageAssetIds.filter((currentAssetId) => currentAssetId !== assetId)
    const removedPrimary = selectedPrimaryReferenceAssetId === assetId
    try {
      await persistTypedSetup({
        productImageAssetIds: nextProductImageAssetIds,
        primaryReferenceAssetId: removedPrimary ? null : selectedPrimaryReferenceAssetId,
        screenshotAssetId: removedPrimary ? null : undefined,
        includeScreenshotAssetId: removedPrimary,
      })
    } catch (error: any) {
      toast({
        title: 'Failed to remove image',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleClearPrimaryReference = async () => {
    try {
      await persistTypedSetup({
        primaryReferenceAssetId: null,
        screenshotAssetId: null,
        includeScreenshotAssetId: true,
      })
    } catch (error: any) {
      toast({
        title: 'Failed to clear primary reference',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleClearLogo = async () => {
    try {
      if (productType === 'finance') {
        await persistTypedSetup({
          logoAssetId: null,
          primaryReferenceAssetId: null,
          screenshotAssetId: null,
          includeScreenshotAssetId: true,
        })
      } else {
        await persistTypedSetup({ logoAssetId: null })
      }
    } catch (error: any) {
      toast({
        title: 'Failed to clear logo',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleGenerateIdeas = async () => {
    if (!selectedProductId) return
    if (productType !== 'novel' && (!Number.isFinite(ideaCountInput) || ideaCountInput < 1)) {
      toast({
        title: 'Invalid idea count',
        description: 'Choose a value greater than or equal to 1.',
        variant: 'destructive',
      })
      return
    }
    try {
      await startIdeaGeneration(selectedProductId)
    } catch (error: any) {
      setHasStartedCurrentRun(false)
      toast({
        title: 'Failed to start idea generation',
        description:
          error?.response?.data?.detail ||
          error?.response?.data?.error?.message ||
          error?.message ||
          'Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleIdeaCountChange = (value: string) => {
    const parsedValue = Number.parseInt(value, 10)
    if (Number.isNaN(parsedValue)) {
      setIdeaCountInput(0)
      return
    }
    setIdeaCountInput(Math.max(parsedValue, 1))
  }

  const renderChecklistStatusRow = (
    label: string,
    ready: boolean,
    description: string
  ) => (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      </div>
      <div
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
          ready ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`}
      >
        {ready ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
        {createChecklistStatus(ready)}
      </div>
    </div>
  )

  const renderIdeaCard = (idea: Idea) => {
    const mediaUrls = filterValidMediaUrls(idea.media_urls)
    const previewUrl = mediaUrls[0]
    const extraMediaCount = Math.max(mediaUrls.length - 1, 0)

    return (
      <div key={idea.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {previewUrl && (
          <div className="relative aspect-video bg-gray-100">
            {isVideoUrl(previewUrl) ? (
              <video src={previewUrl} className="h-full w-full object-cover" preload="metadata" />
            ) : (
              <img src={previewUrl} alt={idea.title} className="h-full w-full object-cover" />
            )}
            {extraMediaCount > 0 && (
              <div className="absolute bottom-2 right-2 rounded-full bg-black/65 px-2 py-0.5 text-xs font-medium text-white">
                +{extraMediaCount}
              </div>
            )}
          </div>
        )}

        <div className="p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-medium text-gray-900">{idea.title}</h3>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                idea.ad_format === 'video'
                  ? 'bg-spectrum-purple/10 text-spectrum-purple'
                  : 'bg-spectrum-blue/10 text-spectrum-blue'
              }`}
            >
              {idea.ad_format}
            </span>
          </div>
          <p className="mt-2 text-sm text-gray-600">{idea.description}</p>
          {idea.tags?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {idea.tags.slice(0, 5).map((tag) => (
                <span key={tag} className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">
                  {tag}
                </span>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-400">{formatDateTime(idea.created_at)}</p>
        </div>
      </div>
    )
  }

  const renderMarketAnalysisPanel = () => (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setAnalysisPanelExpanded((current) => !current)}
        className="flex w-full items-start justify-between gap-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900">Market Analysis Report (Optional)</p>
            {hasUnsavedAnalysisChanges ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-700">Draft</span>
            ) : savedAnalysisSnapshot ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700">Saved</span>
            ) : null}
          </div>
        </div>
        <div className="mt-0.5 text-gray-500">
          {analysisPanelExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {analysisPanelExpanded && (
        <div className="mt-4 min-w-0 space-y-3">
          <p className="text-xs text-gray-500">
            Paste text or upload a file (.txt, .md, .doc, .docx) to guide idea generation. This report is
            commercial guidance only; the novel remains the source of truth.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={analysisFileInputRef}
              type="file"
              accept=".txt,.md,.doc,.docx"
              onChange={handleAnalysisFileUpload}
              className="hidden"
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => analysisFileInputRef.current?.click()}
              disabled={isBusy}
            >
              <Upload className="mr-1.5 h-4 w-4" />
              Upload File
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setAnalysisPreview((current) => !current)}
              disabled={!analysisContent.trim()}
            >
              {analysisPreview ? (
                <>
                  <Pencil className="mr-1.5 h-4 w-4" />
                  Edit
                </>
              ) : (
                <>
                  <Eye className="mr-1.5 h-4 w-4" />
                  Preview
                </>
              )}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleClearAnalysisDraft}
              disabled={!analysisContent && !analysisFileName}
            >
              Clear
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleResetAnalysisToSaved}
              disabled={!savedAnalysisSnapshot || !hasUnsavedAnalysisChanges}
            >
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Reset to Saved
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleRemoveSavedAnalysis}
              disabled={!savedAnalysisSnapshot || removeMarketAnalysisMutation.isPending}
            >
              {removeMarketAnalysisMutation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-1.5 h-4 w-4" />
              )}
              Remove Saved Report
            </Button>
          </div>

          {analysisFileName && analysisContent && <p className="text-xs text-blue-600">Loaded: {analysisFileName}</p>}

          {hasUnsavedAnalysisChanges ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Unsaved changes. Current draft will be saved when you run idea generation.
            </div>
          ) : savedAnalysisSnapshot ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              Saved to product.
            </div>
          ) : null}

          {analysisPreview && analysisContent ? (
            <div className="max-h-80 overflow-auto rounded-xl border border-gray-200 bg-slate-50 p-4">
              <MarkdownArticle markdown={analysisContent} />
            </div>
          ) : (
            <Textarea
              value={analysisContent}
              onChange={(event) => {
                setAnalysisContent(event.target.value)
                setAnalysisFileName(null)
                setAnalysisFileMimeType(null)
              }}
              placeholder="Paste a market analysis report, audience insight summary, or positioning memo here..."
              className="min-h-[180px]"
              disabled={isBusy}
            />
          )}
        </div>
      )}
    </div>
  )

  const renderScenariosPanel = () => {
    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
            <div className="text-sm text-gray-500">
              <p>
                {selectedNovelScenarios.length} saved scenarios. All saved scenarios are used by default for idea
                generation.
              </p>
              <p className="mt-1">
                Scenario count is chosen automatically by the model based on the novel content, always between 10 and
                20. Delete the weak ones to trim the set.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={handleGenerateScenarios}
                disabled={!selectedProductId || generateNovelScenariosMutation.isPending}
              >
                {generateNovelScenariosMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {hasSavedNovelScenarios ? 'Add More Scenarios' : 'Generate Scenarios'}
              </Button>
            </div>
          </div>
        </div>

        {selectedNovelScenarios.length > 0 && (
          <div className="rounded-xl border border-spectrum-blue/20 bg-gradient-to-r from-spectrum-blue/5 via-white to-spectrum-emerald/5 p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Next Step: Generate Ideas</p>
                <p className="mt-1 text-sm text-gray-600">
                  Use these {selectedNovelScenarios.length} saved scenarios as the basis for idea generation. On the
                  next screen you can choose mixed, image-only, or video-only output before starting the run.
                </p>
              </div>
              <Button type="button" onClick={() => setActiveTab('ideas')} className="shadow-sm">
                Generate Ideas
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {selectedNovelScenarios.length > 0 ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {selectedNovelScenarios.map((scenario) => {
              return (
                <div key={scenario.id} className="rounded-xl border border-emerald-300 bg-white p-4 shadow-sm ring-1 ring-emerald-200 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">{scenario.id}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">{scenario.chapter_reference}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                        Selected
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleDeleteScenario(scenario.id)}
                        disabled={deleteNovelScenarioMutation.isPending}
                      >
                        {deleteNovelScenarioMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <p className="mt-3 text-sm leading-6 text-gray-700">{scenario.scene_description}</p>

                  <div className="mt-4 space-y-3">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Characters</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {scenario.characters.map((character) => (
                          <span key={`${scenario.id}-character-${character}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                            {character}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Emotions</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {scenario.emotions.map((emotion) => (
                          <span key={`${scenario.id}-emotion-${emotion}`} className="rounded-full bg-rose-50 px-2.5 py-1 text-xs text-rose-700">
                            {emotion}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Visual Motifs</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {scenario.visual_motifs.map((motif) => (
                          <span key={`${scenario.id}-motif-${motif}`} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
                            {motif}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 p-6 text-sm text-gray-500">
            No saved scenarios yet. Generate a batch and the model will choose between 10 and 20 scenarios based on
            the novel.
          </div>
        )}
      </div>
    )
  }

  const renderIdeasPanel = () => {
    const visibleIdeas = filteredVerticalIdeas
    const resultProductId = selectedProductId
    const defaultGenerationCount = Math.max(1, Math.min(10, visibleIdeas.length))
    const derivedNovelIdeaCount = Math.max(0, selectedNovelConfirmedScenarioCount)
    const selectedIdeaFormat =
      IDEA_FORMAT_OPTIONS.find((option) => option.value === ideaFormatMode) || IDEA_FORMAT_OPTIONS[0]
    const canGenerateIdeas =
      productType === 'novel'
        ? derivedNovelIdeaCount > 0
        : productType === 'ecommerce'
          ? ecommerceChecklist.ready
          : financeChecklist.ready

    const imageFreestyleSearchParams = new URLSearchParams({
      product: resultProductId,
      vertical_skill: 'default',
      idea_source: verticalIdeaSource,
    })
    const videoFreestyleSearchParams = new URLSearchParams({
      product: resultProductId,
      vertical_skill: 'default',
      idea_source: verticalIdeaSource,
    })
    const ideasPageSearchParams = new URLSearchParams({
      product_id: resultProductId,
      source: verticalIdeaSource,
    })
    if (productType === 'novel' && selectedTag !== 'all') {
      imageFreestyleSearchParams.set('idea_tag', selectedTag)
      videoFreestyleSearchParams.set('idea_tag', selectedTag)
      imageFreestyleSearchParams.set('count', String(defaultGenerationCount))
      videoFreestyleSearchParams.set('count', String(defaultGenerationCount))
      ideasPageSearchParams.set('tag', selectedTag)
    }

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div
            className={`grid gap-4 ${
              productType === 'novel'
                ? 'md:grid-cols-[minmax(0,180px)_minmax(0,220px)_1fr_auto] md:items-end'
                : 'md:grid-cols-[minmax(0,180px)_1fr_auto] md:items-end'
            }`}
          >
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900">
                {productType === 'novel' ? 'Scenario-Linked Ideas' : 'Idea Count'}
              </label>
              {productType === 'novel' ? (
                <div className="rounded-lg border border-gray-200 bg-slate-50 px-3 py-2 text-sm text-gray-700">
                  {derivedNovelIdeaCount} idea{derivedNovelIdeaCount === 1 ? '' : 's'} from confirmed scenarios
                </div>
              ) : (
                <Input
                  type="number"
                  min={1}
                  value={ideaCountInput || ''}
                  onChange={(event) => handleIdeaCountChange(event.target.value)}
                  disabled={!selectedProductId || isIdeaGenerationInFlight}
                />
              )}
            </div>

            {productType === 'novel' && (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-900">Tag</label>
                <select
                  value={selectedTag}
                  onChange={(event) => setSelectedTag(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
                  disabled={!selectedProductId || availableIdeaTags.length === 0}
                >
                  <option value="all">All tags</option>
                  {availableIdeaTags.map((tag) => (
                    <option key={tag} value={tag}>
                      {tag}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="text-sm text-gray-500">
              <p>
                Showing {visibleIdeas.length} of {allVerticalIdeas.length} saved vertical ideas
                {productType === 'novel' && selectedTag !== 'all' ? ` for "${selectedTag}"` : ''}.
              </p>
              <p className="mt-1">
                Existing idea titles are loaded into the prompt to reduce duplicates across runs.
              </p>
              <p className="mt-1">Output type: {selectedIdeaFormat.description}</p>
            </div>

            {!shouldHideInitialGenerateIdeasButton && (
              <Button
                onClick={handleGenerateIdeas}
                disabled={
                  !selectedProductId ||
                  (productType === 'novel' ? derivedNovelIdeaCount < 1 : ideaCountInput < 1) ||
                  isIdeaGenerationInFlight ||
                  !canGenerateIdeas
                }
              >
                {hasAnyVerticalIdeas ? 'Add More Ideas' : 'Generate Ideas'}
              </Button>
            )}
          </div>

          <div className="mt-4 rounded-xl border border-gray-200 bg-slate-50/80 p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Output Type</p>
                <p className="mt-1 text-sm text-gray-500">
                  Choose whether this run should stay mixed, image-only, or video-only before starting generation.
                </p>
              </div>
              <div className="inline-flex flex-wrap rounded-xl border border-gray-200 bg-white p-1">
                {IDEA_FORMAT_OPTIONS.map((option) => {
                  const isActive = ideaFormatMode === option.value
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setIdeaFormatMode(option.value)}
                      disabled={!selectedProductId || isIdeaGenerationInFlight}
                      className={`rounded-lg px-3 py-2 text-sm transition ${
                        isActive
                          ? 'bg-gray-900 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-slate-100 hover:text-gray-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500">{selectedIdeaFormat.description}</p>
          </div>

          {productType !== 'novel' && !canGenerateIdeas && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Complete the setup checklist first. Ecommerce needs confirmed price, selling points, a primary product
              image, and at least two product images. Finance needs a confirmed logo that is also the primary
              reference.
            </div>
          )}

          {productType === 'novel' && !canGenerateIdeas && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Generate scenarios first. All saved scenarios are used automatically unless you delete specific ones.
            </div>
          )}
        </div>

        {(hasStartedCurrentRun || isIdeaGenerationInFlight || activeTaskId) && (
          <>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-gray-900">Run Status</p>
                  <p className="mt-1 text-sm text-gray-500">
                    {startMutation.isPending && !activeTaskId ? 'Preparing idea generation...' : stepLabel(currentStep)}
                  </p>
                </div>
                {(startMutation.isPending || taskStatus === 'pending' || taskStatus === 'processing') && (
                  <Loader2 className="h-5 w-5 animate-spin text-spectrum-emerald" />
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Current Step</p>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {startMutation.isPending && !activeTaskId ? 'Generate Ideas' : stepLabel(currentStep)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Requested</p>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {requestedIdeaCount ||
                    (startMutation.isPending
                      ? productType === 'novel'
                        ? selectedNovelConfirmedScenarioCount
                        : ideaCountInput
                      : 0)}
                </p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">Generated</p>
                <p className="mt-2 text-sm font-medium text-gray-900">{successfulIdeaCount}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-gray-500">{executionMetricLabel}</p>
                <p className="mt-2 text-sm font-medium text-gray-900">{executionMetricValue}</p>
              </div>
            </div>
          </>
        )}

        {selectedProductDetail && (
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <p className="text-sm font-medium text-gray-900">Product</p>
            <p className="mt-2 text-sm text-gray-600">{selectedProductDetail.product_name}</p>
          </div>
        )}

        {taskStatus === 'failed' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {resultQuery.data?.error_message || 'Idea generation failed.'}
          </div>
        )}

        {visibleIdeas.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(`/creative-lab/ideas?${ideasPageSearchParams.toString()}`)}
              >
                Open Ideas
              </Button>
              <Button
                variant="outline"
                className="border-spectrum-blue/30 bg-spectrum-blue/5 text-spectrum-blue shadow-sm hover:bg-spectrum-blue/10 hover:text-spectrum-blue hover:shadow-md"
                onClick={() => navigate(`/generate/image/freestyle?${imageFreestyleSearchParams.toString()}`)}
              >
                <ImageIcon className="mr-1.5 h-4 w-4" />
                Generate Image
              </Button>
              <Button
                variant="outline"
                className="border-spectrum-purple/30 bg-spectrum-purple/5 text-spectrum-purple shadow-sm hover:bg-spectrum-purple/10 hover:text-spectrum-purple hover:shadow-md"
                onClick={() => navigate(`/generate/video/freestyle?${videoFreestyleSearchParams.toString()}`)}
              >
                <Clapperboard className="mr-1.5 h-4 w-4" />
                Generate Video
              </Button>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <ImageIcon className="h-4 w-4 text-spectrum-blue" />
                <span>{visibleIdeas.filter((idea) => idea.ad_format === 'image').length} image</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Clapperboard className="h-4 w-4 text-spectrum-purple" />
                <span>{visibleIdeas.filter((idea) => idea.ad_format === 'video').length} video</span>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">{visibleIdeas.map(renderIdeaCard)}</div>
          </>
        ) : hasAnyVerticalIdeas ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 p-6 text-sm text-gray-500">
            {productType === 'novel'
              ? 'No ideas match the selected tag. Choose another tag or switch back to `All tags`.'
              : 'No saved ideas yet for this product.'}
          </div>
        ) : !hasStartedCurrentRun && !isIdeaGenerationInFlight ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 p-6 text-sm text-gray-500">
            {productType === 'novel' && !canGenerateIdeas
              ? 'Generate scenarios first, then choose an idea count and output type before generating ideas.'
              : 'Choose an idea count and output type, then click `Generate Ideas` to start.'}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 p-6 text-sm text-gray-500">
            {isIdeaGenerationInFlight
              ? 'Idea generation is running. Results will appear here once the task finishes.'
              : 'No ideas generated yet. Try running another batch.'}
          </div>
        )}
      </div>
    )
  }

  const renderNovelTextPanel = () => (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm font-medium text-gray-900">Novel Text Preview</p>
        <p className="mt-1 text-sm text-gray-500">Review the full parsed novel text in Markdown format.</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="max-h-[720px] overflow-y-auto rounded-xl border border-gray-200 bg-slate-50 p-5">
          <MarkdownArticle markdown={selectedNovelTextMarkdown || '_No parsed novel text available._'} />
        </div>
      </div>
    </div>
  )

  const renderTypedSetupPanel = () => {
    if (!selectedProductDetail || productType === 'novel') {
      return null
    }

    if (productType === 'finance') {
      return (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Setup Checklist</p>
                <p className="mt-1 text-sm text-gray-500">
                  Confirm the finance brand logo before generating ideas. The confirmed logo becomes the default
                  product reference for image and video generation.
                </p>
              </div>
              <div
                className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                  financeChecklist.ready ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}
              >
                {financeChecklist.ready ? 'Ready' : 'Needs setup'}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {renderChecklistStatusRow(
                'Product name',
                financeChecklist.hasProductName,
                'Keep the product or brand name confirmed before idea generation.'
              )}
              {renderChecklistStatusRow(
                'Confirmed logo',
                financeChecklist.hasLogo,
                'Upload or select the brand logo that should anchor visual generation.'
              )}
              {renderChecklistStatusRow(
                'Primary reference matches logo',
                financeChecklist.primaryMatchesLogo,
                'Finance ideas require the default product reference to be the confirmed logo.'
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <ReferenceImageSelector
              selectedAssets={selectedLogoAsset ? [selectedLogoAsset] : []}
              onSelect={() => setAssetPickerMode('logo')}
              onClearAsset={() => void handleClearLogo()}
              canClearAsset={() => true}
              disabled={isBusy}
              label="Brand Logo"
              addButtonLabel={selectedLogoAsset ? 'Replace logo' : 'Select logo'}
              canAddMore={!selectedLogoAsset}
              helperText="Confirm the real brand logo. This asset will also become the default product reference."
              selectedHelperText="Finance generation should stay credible, restrained, and logo-accurate. Avoid exaggeration, fraud, or guaranteed-return framing."
            />
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
            Finance ideas must stay honest and non-fraudulent. Avoid guaranteed returns, risk-free promises, or
            deceptive urgency.
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900">Setup Checklist</p>
              <p className="mt-1 text-sm text-gray-500">
                Confirm the product text fields and product images before generating ideas. Upload multiple product
                images and aim to cover multiple angles.
              </p>
            </div>
            <div
              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                ecommerceChecklist.ready ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}
            >
              {ecommerceChecklist.ready ? 'Ready' : 'Needs setup'}
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {renderChecklistStatusRow(
              'Product name',
              ecommerceChecklist.hasProductName,
              'Keep the live product name confirmed before idea generation.'
            )}
            {renderChecklistStatusRow(
              'Price',
              ecommerceChecklist.hasPrice,
              'Confirm the actual sell price that should be reflected in positioning.'
            )}
            {renderChecklistStatusRow(
              'Selling points',
              ecommerceChecklist.hasSellingPoints,
              'At least one clear product selling point is required.'
            )}
            {renderChecklistStatusRow(
              'Primary product image',
              ecommerceChecklist.hasPrimaryReference,
              'Choose the single image that should drive downstream product fidelity.'
            )}
            {renderChecklistStatusRow(
              'Confirmed product images',
              ecommerceChecklist.hasEnoughProductImages,
              'Confirm at least two product images. More angles are recommended.'
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <ReferenceImageSelector
            selectedAssets={selectedPrimaryReferenceAsset ? [selectedPrimaryReferenceAsset] : []}
            onSelect={() => setAssetPickerMode('primary_reference')}
            onClearAsset={() => void handleClearPrimaryReference()}
            canClearAsset={() => true}
            disabled={isBusy}
            label="Primary Product Image"
            addButtonLabel={selectedPrimaryReferenceAsset ? 'Replace primary image' : 'Select primary image'}
            canAddMore={!selectedPrimaryReferenceAsset}
            helperText="Pick the single product image that should anchor visual fidelity in downstream generation."
            selectedHelperText="This asset becomes the default product reference in image and video generation."
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <ReferenceImageSelector
            selectedAssets={selectedProductImageAssets}
            onSelect={() => setAssetPickerMode('product_images')}
            onClearAsset={(asset) => void handleRemoveProductImage(asset.id)}
            canClearAsset={() => true}
            disabled={isBusy}
            label="Confirmed Product Images"
            addButtonLabel="Add product image"
            canAddMore
            multiSelectLayout="horizontal"
            helperText="Upload or select multiple product images. Frontend should encourage complete coverage and multiple angles."
            selectedHelperText="Keep the real product appearance, logo, packaging, color, and structure intact across all later generations."
          />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <ReferenceImageSelector
            selectedAssets={selectedLogoAsset ? [selectedLogoAsset] : []}
            onSelect={() => setAssetPickerMode('logo')}
            onClearAsset={() => void handleClearLogo()}
            canClearAsset={() => true}
            disabled={isBusy}
            label="Logo (Optional)"
            addButtonLabel={selectedLogoAsset ? 'Replace logo' : 'Select logo'}
            canAddMore={!selectedLogoAsset}
            helperText="Optional. Add a logo asset if the product has one and logo fidelity matters in downstream generation."
          />
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-4 text-sm text-blue-800">
          Ecommerce generation must preserve product fidelity. Keep the item recognizable in packaging, materials,
          colors, logo treatment, and structure.
        </div>
      </div>
    )
  }

  const renderWorkspace = () => {
    if (productMode === 'new' && productType === 'novel' && parsedNovelPreview && !selectedProductId) {
      return (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Parsed Novel Preview</p>
                <p className="mt-1 text-sm text-gray-500">
                  Review the extracted novel content, then create the product and generate the first scenario batch.
                </p>
              </div>
              <div className="rounded-full bg-spectrum-emerald/10 px-3 py-1 text-xs font-medium text-spectrum-emerald">
                Preview Ready
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Product Name</p>
              <p className="mt-2 text-sm font-medium text-gray-900">
                {parsedNovelPreview.parsed_fields.product_name || 'Untitled'}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Language</p>
              <p className="mt-2 text-sm font-medium text-gray-900">
                {parsedNovelPreview.parsed_fields.language || 'Unknown'}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Style</p>
              <p className="mt-2 text-sm font-medium text-gray-900">
                {parsedNovelPreview.parsed_fields.novel_style || 'General fiction'}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-xs uppercase tracking-wide text-gray-500">Characters</p>
              <p className="mt-2 text-sm font-medium text-gray-900">
                {parsedNovelPreview.raw_text.length.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-900">Markdown Content</p>
              <p className="mt-1 text-sm text-gray-500">Review the extracted novel content before continuing.</p>
            </div>
            <div className="max-h-[560px] overflow-y-auto rounded-xl border border-gray-200 bg-slate-50 p-5">
              <MarkdownArticle markdown={novelPreviewMarkdown} />
            </div>
          </div>
        </div>
      )
    }

    if (!selectedProductId) {
      return (
        <div className="flex min-h-[520px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white/70 px-8 text-center">
          <div className="rounded-2xl bg-spectrum-emerald/10 p-4 text-spectrum-emerald">
            {productType === 'novel' ? <BookOpen className="h-7 w-7" /> : <Landmark className="h-7 w-7" />}
          </div>
          <h2 className="mt-5 text-xl font-semibold text-gray-900">
            {productType === 'novel'
              ? 'Vertical Ideas'
              : productType === 'finance'
                ? 'Finance Vertical Ideas'
                : 'Ecommerce Vertical Ideas'}
          </h2>
          <p className="mt-2 max-w-xl text-sm text-gray-500">
            {productType === 'novel'
              ? 'Create a new novel product or choose an existing one to review text, curate scenarios, and generate ideas.'
              : productType === 'finance'
                ? 'Create or select a finance product, confirm the logo, then generate compliant brand-safe ideas.'
                : 'Create or select an ecommerce product, confirm the product images, then generate fidelity-safe ideas.'}
          </p>
        </div>
      )
    }

    if (productType === 'novel') {
      return (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {selectedProductDetail?.product_name || 'Novel Product'}
                </p>
                {selectedProductDetail?.description && (
                  <p className="mt-1 text-sm text-gray-500">{selectedProductDetail.description}</p>
                )}
                <div className="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                  Style: {currentNovelStyle}
                </div>
              </div>
              <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
                <button
                  type="button"
                  onClick={() => setActiveTab('text')}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    activeTab === 'text' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Text
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('scenarios')}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    activeTab === 'scenarios'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Scenarios
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('ideas')}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    activeTab === 'ideas' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Ideas
                </button>
              </div>
            </div>
          </div>

          {renderMarketAnalysisPanel()}

          {activeTab === 'text'
            ? renderNovelTextPanel()
            : activeTab === 'scenarios'
              ? renderScenariosPanel()
              : renderIdeasPanel()}
        </div>
      )
    }

    return (
      <div className="space-y-6">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-gray-900">
                {selectedProductDetail?.product_name || 'Vertical Product'}
              </p>
              {selectedProductDetail?.description && (
                <p className="mt-1 text-sm text-gray-500">{selectedProductDetail.description}</p>
              )}
              <div className="mt-2 inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                Source mode: {selectedTypedSourceMode}
              </div>
            </div>
          </div>
        </div>

        {renderTypedSetupPanel()}
        {renderIdeasPanel()}
      </div>
    )
  }

  const renderWizardChoiceCard = (params: {
    title: string
    description: string
    selected: boolean
    onClick: () => void
    badge?: string
  }) => (
    <button
      type="button"
      onClick={params.onClick}
      className={`group w-full rounded-2xl border px-4 py-3 text-left transition ${
        params.selected
          ? 'border-spectrum-emerald bg-gradient-to-br from-spectrum-emerald/12 via-white to-spectrum-lime/12 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">{params.title}</p>
            {params.badge && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                {params.badge}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs leading-5 text-gray-500">{params.description}</p>
        </div>
        <div
          className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full border transition ${
            params.selected
              ? 'border-spectrum-emerald bg-spectrum-emerald text-white'
              : 'border-gray-200 bg-white text-gray-300 group-hover:border-gray-300 group-hover:text-gray-400'
          }`}
        >
          {params.selected ? <CheckCircle2 className="h-4 w-4" /> : <ArrowRight className="h-3.5 w-3.5" />}
        </div>
      </div>
    </button>
  )

  const renderWizardNavigation = () => (
    <div className="flex items-center justify-between pt-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={goToPreviousWizardStep}
        disabled={wizardStepIndex === 0}
        className="px-2"
      >
        <ArrowLeft className="mr-1.5 h-4 w-4" />
        Previous
      </Button>
      {!isLastWizardStep && (
        <Button
          type="button"
          size="sm"
          onClick={goToNextWizardStep}
          disabled={!canAdvanceWizard}
          className="px-3"
        >
          Next
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      )}
    </div>
  )

  const renderWizardStageContent = () => {
    if (currentWizardStep.key === 'product_mode') {
      return (
        <div className="space-y-3">
          {renderWizardChoiceCard({
            title: 'Use Existing',
            description:
              productType === 'novel'
                ? 'Use a saved novel product and continue into text review, scenario curation, or idea generation.'
                : 'Use a saved product and continue into the setup checklist.',
            selected: productMode === 'existing',
            onClick: () => handleProductModeChange('existing'),
          })}
          {renderWizardChoiceCard({
            title: 'Create New',
            description:
              productType === 'novel'
                ? 'Parse a fresh novel source, create the product, then generate scenarios.'
                : 'Import from URL or create manually, then finish setup.',
            selected: productMode === 'new',
            onClick: () => handleProductModeChange('new'),
          })}
        </div>
      )
    }

    if (currentWizardStep.key === 'existing_product') {
      return (
        <div className="space-y-3">
          <div>
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">
              {productType === 'novel'
                ? 'Novel Product'
                : productType === 'finance'
                  ? 'Finance Product'
                  : 'Ecommerce Product'}
            </label>
            <select
              value={selectedProductId}
              onChange={(event) => handleExistingProductChange(event.target.value)}
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-spectrum-emerald focus:outline-none"
            >
              <option value="">Select a product...</option>
              {filteredProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </div>
          {selectedProductId ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
              Product selected. Continue in the workspace on the right.
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-500">
              Choose a saved product to continue.
            </div>
          )}
        </div>
      )
    }

    if (currentWizardStep.key === 'novel_source') {
      return (
        <div className="space-y-3">
          {renderWizardChoiceCard({
            title: 'URL',
            description: 'Fetch novel text from a webpage URL.',
            selected: novelSourceMode === 'url',
            onClick: () => handleNovelSourceModeChange('url'),
            badge: 'Web',
          })}
          {renderWizardChoiceCard({
            title: 'Text',
            description: 'Paste novel text directly without uploading a file.',
            selected: novelSourceMode === 'text',
            onClick: () => handleNovelSourceModeChange('text'),
            badge: 'Paste',
          })}
          {renderWizardChoiceCard({
            title: 'File',
            description: 'Upload a txt, md, docx, or pdf novel source file.',
            selected: novelSourceMode === 'file',
            onClick: () => handleNovelSourceModeChange('file'),
            badge: 'Upload',
          })}
        </div>
      )
    }

    if (currentWizardStep.key === 'typed_create_mode') {
      return (
        <div className="space-y-3">
          {renderWizardChoiceCard({
            title: 'URL',
            description: 'Reuse the existing URL parsing workflow.',
            selected: typedCreateMode === 'url',
            onClick: () => handleTypedCreateModeChange('url'),
            badge: 'Import',
          })}
          {renderWizardChoiceCard({
            title: 'Manual',
            description: 'Reuse the existing manual product form.',
            selected: typedCreateMode === 'manual',
            onClick: () => handleTypedCreateModeChange('manual'),
            badge: 'Form',
          })}
        </div>
      )
    }

    if (currentWizardStep.key === 'novel_input') {
      return (
        <div className="space-y-3">
          {novelSourceMode === 'url' ? (
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">Source URL</label>
              <Input
                value={sourceUrl}
                onChange={(event) => {
                  setSourceUrl(event.target.value)
                  if (parsedNovelPreview) clearNovelPreview()
                }}
                placeholder="https://example.com/novel-page"
              />
            </div>
          ) : novelSourceMode === 'file' ? (
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">Upload File</label>
              <label className="flex cursor-pointer items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center hover:border-spectrum-blue">
                <input
                  type="file"
                  accept=".txt,.md,.docx,.pdf"
                  className="hidden"
                  onChange={(event) => {
                    setSourceFile(event.target.files?.[0] || null)
                    if (parsedNovelPreview) clearNovelPreview()
                  }}
                />
                <div>
                  <Upload className="mx-auto h-5 w-5 text-gray-500" />
                  <p className="mt-2 text-sm text-gray-700">
                    {sourceFile ? sourceFile.name : 'Choose a novel source file'}
                  </p>
                </div>
              </label>
            </div>
          ) : (
            <div>
              <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">Novel Text</label>
              <Textarea
                value={sourceText}
                onChange={(event) => {
                  setSourceText(event.target.value)
                  if (parsedNovelPreview) clearNovelPreview()
                }}
                placeholder="Paste the novel text here..."
                className="min-h-[240px]"
              />
              <p className="mt-2 text-xs text-gray-500">
                Paste plain text or markdown directly, then parse it to generate the novel preview.
              </p>
            </div>
          )}

          {renderMarketAnalysisPanel()}

          <Button
            onClick={handleCreateNewPrimaryAction}
            disabled={
              isBusy ||
              (novelSourceMode === 'url' && !sourceUrl.trim()) ||
              (novelSourceMode === 'file' && !sourceFile) ||
              (novelSourceMode === 'text' && !sourceText.trim())
            }
            className="w-full"
          >
            {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {parsedNovelPreview ? 'Create Product & Generate Scenarios' : 'Parse & Preview Novel Content'}
          </Button>
        </div>
      )
    }

    return typedCreateMode === 'url' ? (
      <ProductImportFromUrlCard
        title={productType === 'finance' ? 'Import Finance Product' : 'Import Ecommerce Product'}
        description={
          productType === 'finance'
            ? 'Import a finance product or brand page with the existing URL parser, then confirm the logo in the workspace.'
            : 'Import a product page with the existing URL parser, then confirm product images and the primary reference in the workspace.'
        }
        density="compact"
        url={typedSourceUrl}
        onUrlChange={setTypedSourceUrl}
        onSubmit={() => void handleCreateTypedFromUrl()}
        isPending={createTypedProductFromUrlMutation.isPending}
        isError={createTypedProductFromUrlMutation.isError}
      />
    ) : (
      <div className="space-y-3">
        <ManualProductEditor
          value={typedManualProduct}
          onChange={setTypedManualProduct}
          disabled={createTypedManualProductMutation.isPending}
          density="compact"
          basicSectionTitle={productType === 'finance' ? 'Finance Product Basics' : 'Ecommerce Product Basics'}
        />
        <Button
          onClick={() => void handleCreateTypedManual()}
          disabled={!typedManualProduct.product_name.trim() || createTypedManualProductMutation.isPending}
          className="w-full"
        >
          {createTypedManualProductMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Product
        </Button>
      </div>
    )
  }

  return (
    <>
      {(createTypedProductFromUrlMutation.isPending || createNovelProductMutation.isPending) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-sm rounded-lg bg-white p-8 text-center">
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium">
              {productType === 'novel' ? 'Preparing novel product ...' : 'Importing product ...'}
            </p>
            <p className="mt-2 text-sm text-gray-500">This usually takes 30-60 seconds</p>
          </div>
        </div>
      )}

      <AssetPickerModal
        open={assetPickerMode !== null}
        onClose={() => setAssetPickerMode(null)}
        onSelect={(asset) => void handleSetupAssetSelected(asset)}
        title={
          assetPickerMode === 'logo'
            ? 'Select Logo'
            : assetPickerMode === 'primary_reference'
              ? 'Select Primary Product Image'
              : 'Add Product Image'
        }
        filterProductId={selectedProductId || undefined}
        assetType="image"
      />

      <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Vertical Setup</h2>
          </div>

          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">Product Type</p>
              <select
                value={productType}
                onChange={(event) => handleProductTypeChange(event.target.value as VerticalIdeasProductType)}
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-spectrum-emerald focus:outline-none"
              >
                <option value="novel">Novel</option>
                <option value="ecommerce">Ecommerce</option>
                <option value="finance">Finance</option>
              </select>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-slate-50 via-white to-slate-50 p-4">
              <div className="space-y-2">
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-gray-500">Guided Flow</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{currentWizardStep.title}</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">{currentWizardStep.description}</p>
                </div>
                <div className="inline-flex self-start rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 shadow-sm">
                  Step {Math.min(wizardStepIndex + 1, wizardSteps.length)}/{wizardSteps.length}
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2">
                {wizardSteps.map((step, index) => {
                  const isCompleted = index < wizardStepIndex
                  const isActive = index === wizardStepIndex
                  return (
                    <div key={step.key} className="flex min-w-0 flex-1 items-center gap-2">
                      <div
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${
                          isCompleted
                            ? 'border-gray-900 bg-gray-900 text-white'
                            : isActive
                              ? 'border-spectrum-emerald bg-spectrum-emerald text-white'
                              : 'border-gray-200 bg-white text-gray-400'
                        }`}
                      >
                        {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                      </div>
                      <div className="min-w-0">
                        <p className={`truncate text-xs font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                          {step.label}
                        </p>
                      </div>
                      {index < wizardSteps.length - 1 && <div className="h-px flex-1 bg-gray-200" />}
                    </div>
                  )
                })}
              </div>
            </div>

            {wizardSummaryItems.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {wizardSummaryItems.map((item) => (
                  <div
                    key={`${item.label}-${item.value}`}
                    className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] text-gray-600"
                  >
                    <span className="font-medium text-gray-800">{item.label}:</span> {item.value}
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-3">
                <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">
                  {currentWizardStep.label}
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-900">{currentWizardStep.title}</p>
              </div>

              {renderWizardStageContent()}

              {renderWizardNavigation()}
            </div>

            {productMode === 'existing' && selectedProductId && (
              <p className="text-xs text-gray-500">
                {productType === 'novel'
                  ? 'Use the workspace on the right to review text, curate scenarios, or generate more ideas.'
                  : 'Use the workspace on the right to finish setup, then generate ideas and move into image or video freestyle.'}
              </p>
            )}
          </div>
        </section>

        {renderWorkspace()}
      </div>
    </>
  )
}
